/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useContext, useMemo } from 'react';
import {
    Au24KBreakdownRow,
    Au24KData,
    ControlRoomData,
    DailyCashCheck,
    DailyMovementsGroup,
    DailyBalance,
    DailyStats,
    DeliveredNotInvoiced,
    FinanceGroupData,
    FinanceNote,
    FinancialFlow,
    FinancialItem,
    GrandTotals,
} from '../domain/controlRoom';
import { type ContractDocumentEngine, type InvoiceDocumentEngine } from '../domain/documentEngines';
import {
    buildAuditLogSearchParams,
    type AuditLogCatalogue,
    type AuditLogEntry,
    type AuditLogFilters,
    type AuditLogListItem,
    type AuditLogPagination,
} from '../domain/auditLog';
import type { LabelPrinterConfigPayload } from '../domain/labelPrinterConfig';
import type { CashPointAlertSettingsPayload } from '../domain/cashPointAlertSettings';
import type { LoyaltySettingsPayload } from '../domain/loyaltySettings';
import type { ClientStatisticsResponse } from '../domain/statisticsClients';
import type { InventoryTrendsResponse } from '../domain/statisticsInventory';
import type { OperationsResponse } from '../domain/statisticsOperations';
import type { IncomingSummaryGroup } from '../domain/metals/incomingSummary';
import type {
    PurchaseCashPoint,
    PurchaseRequest,
    PurchaseResult,
} from '../domain/metals/metalPurchase';
import {
    purchaseListQueryString,
    type PurchaseListItem,
    type PurchaseListRange,
} from '../domain/metals/purchaseList';
import type {
    MetalCategory,
    MetalCategoryDates,
    MetalDateSelection,
    MetalInventoryItem,
} from '../domain/metals/metalCategories';
import type { StatusRefreshOutcome } from '../domain/statusRefresh';
import { apiErrorMessage } from '../domain/apiErrorMessage';
import type { ExpensesResponse, GoodsSalesResponse } from '../domain/statisticsExpenses';
import i18n from '../i18n';
import { useAuth } from './AuthContext';

/** Translates a refusal key in the language the operator has chosen (`apiErrorMessage`). */
const translateKey = (key: string): string => i18n.t(key);

type StatisticsTimeframe = 'daily' | 'weekly' | 'monthly' | 'yearly';

interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface InventoryItem {
    id: number;
    name: string;
    description?: string;
    category: string;
    weight?: number;
    value: number;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface Metal {
    id: number;
    type: string;
    weight: number;
    purity: number;
    value: number;
    date: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface DashboardStats {
    totalClients: number;
    activeContracts: number;
    totalValue: number;
    monthlyRevenue: number;
}

interface FinancialReport {
    period: string;
    income: number;
    expenses: number;
    profit: number;
    transactions: number;
}

interface ClientReport {
    clientId: number;
    clientName: string;
    totalContracts: number;
    totalValue: number;
    status: string;
}

interface InventoryReport {
    category: string;
    itemCount: number;
    totalValue: number;
    averageValue: number;
}

interface StatsOverview {
    totalRevenue: number;
    totalClients: number;
    activeContracts: number;
    inventoryValue: number;
}

interface MonthlyStats {
    month: number;
    year: number;
    revenue: number;
    newClients: number;
    newContracts: number;
}

interface YearlyStats {
    year: number;
    totalRevenue: number;
    totalClients: number;
    totalContracts: number;
    months: MonthlyStats[];
}

interface TopClient {
    id: number;
    name: string;
    totalValue: number;
    contractCount: number;
}

interface TopItem {
    id: number;
    name: string;
    category: string;
    value: number;
    frequency: number;
}

interface MenuItem {
    Id: number;
    Name: string;
    Parent: number;
    Enabled: number;
    Level: number;
    Hasc: number;
    Visible: number;
    Open: number;
    formno: number;
    form?: string;
    Button?: string;
    Secured: number;
}

export interface IncomingMetal {
    date: string;
    type: string;
    weight: number;
    value: number;
}

export interface IncomingMetalReport {
    date: string;
    grouped: IncomingSummaryGroup[];
}

export interface InventorySummaryRow {
    metal_type: string;
    totalQty: number;
    pmp: number;
    qty999: number;
    totalLei: number;
}

export interface InventoryEntryItem {
    id: number;
    date: string;
    metal_type: string;
    fineness: number;
    gross_quantity: number;
    unit_price: number;
    contract_number?: string | null;
    contract_date?: string | null;
    status?: string;
}

interface StatutsExecutionResult {
    success: boolean;
    message: string;
    executionTime: string;
    timestamp: string;
}

interface StatutsStatus {
    success: boolean;
    message: string;
    cronSchedule: string;
    description: string;
    /** The latest refresh in the backend process; `null` until one has finished. */
    lastRun: StatusRefreshOutcome | null;
    timestamp: string;
}

/**
 * What one inventory date contributes to one metal and fineness of the stock; the figures arrive
 * as the decimals MySQL returns them.
 */
export interface StockState {
    metal_type: string;
    fineness: number | string;
    /** `YYYY-MM-DD`. */
    inventory_date: string;
    /** The purchase the row came from; `null` for the contracts' metal. */
    purchase_id?: number | null;
    quantity: number | string;
    purified_quantity: number | string;
    value_ron: number | string;
}

export interface StockResponse {
    items: StockState[];
    /** The latest BNR rate, RON per EUR; `null` when none is stored. */
    exchangeRate: number | null;
}

interface MetalStock {
    MetalType: string;
    Karat: number;
    totalMass: number;
    avgPmp: number;
    earliestDate: string;
}

interface BackendStatisticsResponse {
    dailyRevenue: Array<{
        date: string;
        revenue: number;
        contracts: number;
        /** Set on the running month, whose partial totals were extrapolated to a full month. */
        projected?: boolean;
    }>;
    contractsByStatus: Array<{ status: string; count: number; value: number }>;
    topClients: Array<{ name: string; contracts: number; value: number }>;
    goldData: Array<{ karat: string; weight: number; percentage: number }>;
    monthlyTrends: Array<{ month: string; revenue: number; profit: number }>;
    nonMetalContracts: Array<{ date: string; value: number; count: number }>;
    metalContracts: Array<{ date: string; value: number; count: number }>;
    metalsIn24k: Array<{ date: string; grams: number }>;
    contractsPaid: Array<{ date: string; value: number }>;
    dailyPaidContractsValue: Array<{ date: string; value: number }>;
    dailyPaidContractsCount: Array<{ date: string; count: number }>;
    paidCommissionValue: Array<{ date: string; value: number }>;
    paidCommissionCount: Array<{ date: string; count: number }>;
    paidContractCommission: Array<{ date: string; value: number }>;
    totalInterestPaid: Array<{ date: string; value: number }>;
}

// Business Intelligence types
interface GoldPricingData {
    avgPricePerGramEur: number;
    totalGoldValueEur: number;
    totalGoldGrams: number;
    totalFineGoldGrams: number;
    totalGoldContracts: number;
}

interface CustomerTrendData {
    month: string;
    new_customers: number;
    contracts_created: number;
    total_loan_value_ron: number;
}

interface ProfitabilityTrendData {
    date: string;
    revenueRon: number;
    interestIncomeRon: number;
    lostContractsRon: number;
    netProfitRon: number;
}

interface ContractLifecycleData {
    avgDurationDays: number;
    redemptionRate: number;
    finishedRedemptionRate: number;
    extensionRate: number;
    inventoryRate: number;
    avgExtensionsPerContract: number;
}

interface CustomerSegmentData {
    segment: string;
    customer_count: number;
    total_contracts: number;
    total_value_ron: number;
    avg_contract_value_ron: number;
    redemption_rate: number;
}

interface GoldPriceSimulationData {
    priceChangePercent: number;
    estimatedNewCustomers: number;
    estimatedNewContracts: number;
    estimatedNewRevenueRon: number;
    currentAvgPricePerGram: number;
    simulatedPricePerGram: number;
}

interface TopCustomerData {
    client_id: number;
    name: string;
    contracts: number;
    prolongation_interest_ron: number;
    redemption_profit_ron: number;
    profit_ron: number;
}

interface MetalDistributionData {
    metal_category: string;
    contract_count: number;
    total_grams: number;
    total_value_ron: number;
    avg_price_per_gram: number;
}

export interface BusinessIntelligenceResponse {
    currency?: 'EUR' | 'LEI';
    goldPricing: GoldPricingData;
    customerTrends: CustomerTrendData[];
    profitabilityTrends: ProfitabilityTrendData[];
    lifecycle: ContractLifecycleData;
    customerSegments: CustomerSegmentData[];
    priceSimulations: GoldPriceSimulationData[];
    topCustomers: TopCustomerData[];
    metalDistribution: MetalDistributionData[];
}

// Advanced Analytics Types
export interface SeasonalityData {
    period: string;
    avgContracts: number;
    avgRevenue: number;
    avgRedemptions: number;
    peakIndicator: number; // 1-5 scale, 5 being highest
}

export interface WeeklyPatternData {
    day_num: number;
    day_name: string;
    avg_contracts: number;
    avg_revenue: number;
}

export interface ForecastData {
    period: string;
    predictedContracts: number;
    predictedRevenue: number;
    confidenceLower: number;
    confidenceUpper: number;
    growthRate: number;
}

export interface RiskMetrics {
    defaultRatePercent: number;
    avgDaysToDefault: number;
    atRiskContractsCount: number;
    atRiskValueRon: number;
    riskTrend: string; // 'improving' | 'stable' | 'worsening'
    portfolioHealthScore: number; // 0-100
}

export interface CashFlowPrediction {
    period: string;
    expectedInflows: number;
    expectedOutflows: number;
    netCashFlow: number;
    cumulativeCashPosition: number;
}

export interface RetentionMetrics {
    period: string;
    newCustomers: number;
    returningCustomers: number;
    retentionRate: number;
    churnRate: number;
    avgContractsPerCustomer: number;
}

export interface AdvancedAnalyticsResponse {
    seasonality: SeasonalityData[];
    weeklyPattern: WeeklyPatternData[];
    forecast: ForecastData[];
    riskMetrics: RiskMetrics;
    cashFlowPredictions: CashFlowPrediction[];
    retention: RetentionMetrics[];
    recommendations: string[];
}

// Gold Value Statistics types
export interface GoldValueTimeSeriesData {
    date: string;
    estimated: boolean;
    active24k: number;
    passive24k: number;
    expired24k: number;
    activePassive24k: number;
    activeEuro: number;
    activeLei: number;
    passiveEuro: number;
    passiveLei: number;
    expiredEuro: number;
    expiredLei: number;
    activePassiveEuro: number;
    activePassiveLei: number;
    totalEuro: number;
    totalLei: number;
    cashBalanceEur: number | null;
    cashBalanceRon: number | null;
    bankBalanceEur: number | null;
    bankBalanceRon: number | null;
    expiredInventoryValueEur: number;
    expiredInventoryValueRon: number;
}

export interface GoldValueSnapshotData {
    active24k: number;
    passive24k: number;
    expired24k: number;
    activePassive24k: number;
    avgGramValueEur: number;
    avgGramValueRon: number;
    totalValueEur: number;
    totalValueRon: number;
    cashBalanceEur: number;
    cashBalanceRon: number;
    bankBalanceEur: number;
    bankBalanceRon: number;
    expiredInventoryValueEur: number;
    expiredInventoryValueRon: number;
    expiredGoldPctOfMonthlyAvgEur: number;
    expiredGoldPctOfMonthlyAvgRon: number;
}

export interface MonthlyExpiredGoldPctData {
    month: string;
    avgActivePassiveEur: number;
    avgActivePassiveLei: number;
    expiredEur: number;
    expiredLei: number;
    pctEur: number;
    pctLei: number;
}

export interface GoldValueResponse {
    timeSeries: GoldValueTimeSeriesData[];
    snapshot: GoldValueSnapshotData;
    monthlyExpiredGoldPct: MonthlyExpiredGoldPctData[];
}

// Gold Price Change Simulation types
export interface GoldPriceSimulationKaratBreakdown {
    karatLabel: string;
    purity: number;
    totalGrams: number;
    totalNetGrams: number;
    totalContracts: number;
    currentPricePerGram: number;
    currentTotalValue: number;
    newPricePerGram: number;
    newTotalValue: number;
    additionalCashNeeded: number;
}

export interface GoldPriceSimulationCurrentPrice {
    metal: string;
    karatPercent: number;
    price: number;
    extendedPrice: number;
}

export interface GoldPriceChangeSimulationResponse {
    karatBreakdown: GoldPriceSimulationKaratBreakdown[];
    summary: {
        totalGoldGrams: number;
        totalNetGoldGrams: number;
        totalContracts: number;
        currentTotalValue: number;
        newTotalValue: number;
        totalAdditionalCash: number;
    };
    currentPrices: GoldPriceSimulationCurrentPrice[];
}

// Cash Demand Projection types
export interface CashDemandProjectionMonth {
    month: string;
    contractsExpiring: number;
    netGramsExpiring: number;
    currentValueExpiring: number;
    newValueExpiring: number;
    monthlyAdditionalCash: number;
    cumulativeAdditionalCash: number;
}

/** `POST /statistics/refresh`: whether the snapshots were taken again, and when they last were. */
export interface StatisticsRefreshResponse {
    success: boolean;
    message: string;
    refreshed: boolean;
    /** End of the last complete refresh, shop clock `YYYY-MM-DD HH:mm:ss`, or null. */
    refreshedAt: string | null;
    details: {
        financeHistory: boolean;
        dailyStatistics: boolean;
        contractStatus: boolean;
        bilant: boolean;
        au24kSnapshot: boolean;
        indexSnapshot: boolean;
    } | null;
}

export interface CashDemandProjectionResponse {
    months: CashDemandProjectionMonth[];
    totalContractsProjected: number;
    totalNetGramsProjected: number;
    totalCurrentValue: number;
    totalNewValue: number;
    totalAdditionalCash: number;
}

// API parameter types
interface QueryParams {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    status?: string;
    category?: string;
    startDate?: string;
    endDate?: string;
}

interface ReportParams extends QueryParams {
    format?: 'json' | 'excel' | 'pdf';
    dateRange?: string;
    start_date?: string;
    end_date?: string;
}

interface ApiResponse<T = unknown> {
    success: boolean;
    error?: string;
    data?: T;
    message?: string;
}

/**
 * The per-cash-point configuration endpoints always answer with the standard `{ success, data }`
 * envelope.
 */
type CashPointConfigEnvelope<T> = {
    success: boolean;
    data?: T;
    error?: string;
};

/**
 * Unwrap the envelope, treating a success response with no payload as a failure.
 *
 * The label printer endpoints are the only source of the stock catalogue, so an empty body would
 * otherwise render a stock select with no options and read as "this printer supports no
 * media" rather than as a failed request; an empty alert settings body would show a cash point's
 * alert as switched off.
 */
function unwrapCashPointConfig<T>(res: ApiResponse<CashPointConfigEnvelope<T>>): ApiResponse<T> {
    if (!res.success) {
        return { success: false, error: res.error };
    }

    if (res.data?.data === undefined) {
        return { success: false, error: res.data?.error ?? 'Malformed response' };
    }

    return { success: true, data: res.data.data };
}

/**
 * The daily report's "current session" filter: the backend narrows the report to the session
 * open at this workstation's cash point, so the cash point travels with the flag.
 */
function appendDailyReportSessionFilter(
    queryParams: URLSearchParams,
    params: { sessionFilter?: boolean; cashPointId?: number | null },
): void {
    if (!params.sessionFilter) {
        return;
    }
    queryParams.append('sessionFilter', 'true');
    if (params.cashPointId != null) {
        queryParams.append('cashPointId', String(params.cashPointId));
    }
}

interface AuditLogEnvelope<T> {
    success: boolean;
    data: T;
}

/** An operation-log answer's payload; `fallbackError` names the request when the client gave none. */
function unwrapAuditLogResponse<T>(
    response: ApiResponse<AuditLogEnvelope<T>>,
    fallbackError: string,
): ApiResponse<T> {
    if (!response.success || !response.data?.success) {
        return { success: false, error: response.error ?? fallbackError };
    }
    return { success: true, data: response.data.data };
}

interface RequestOptions extends RequestInit {
    headers?: Record<string, string>;
}

export interface ContractDocumentTemplatePayload {
    id: number;
    name: string;
    engine: string;
    htmlContent: string;
    cssContent: string | null;
    isActive: boolean;
    isDraft: boolean;
    createdAt?: string | null;
    updatedAt?: string | null;
}

export interface ContractDocumentAssetPayload {
    id: number;
    templateId: number;
    assetType: 'logo' | 'page_background';
    pageNumber: number | null;
    mimeType: string;
    sizeBytes: number;
    createdAt?: string | null;
    updatedAt?: string | null;
}

export type InvoiceDocumentTemplatePayload = ContractDocumentTemplatePayload;

interface ApiContextType {
    makeRequest: <T = unknown>(
        endpoint: string,
        options?: RequestOptions,
    ) => Promise<ApiResponse<T>>;
    inventory: {
        getAll: (
            params?: QueryParams,
        ) => Promise<ApiResponse<{ items: InventoryItem[]; pagination: Pagination }>>;
        getById: (id: string | number) => Promise<ApiResponse<InventoryItem>>;
        create: (itemData: Partial<InventoryItem>) => Promise<ApiResponse<InventoryItem>>;
        update: (
            id: string | number,
            itemData: Partial<InventoryItem>,
        ) => Promise<ApiResponse<InventoryItem>>;
        delete: (id: string | number) => Promise<ApiResponse<void>>;
        search: (term: string) => Promise<ApiResponse<InventoryItem[]>>;
    };
    metals: {
        getAll: (params?: QueryParams) => Promise<ApiResponse<Metal[]>>;
        getIncoming: (
            date?: string,
        ) => Promise<ApiResponse<{ date: string; metals: IncomingMetal[] }>>;
        getIncomingDates: () => Promise<ApiResponse<{ dates: string[] }>>;
        getIncomingReport: (date?: string) => Promise<ApiResponse<IncomingMetalReport>>;
        getById: (id: string | number) => Promise<ApiResponse<Metal>>;
        update: (id: string | number, metalData: Partial<Metal>) => Promise<ApiResponse<Metal>>;
        delete: (id: string | number) => Promise<ApiResponse<void>>;
        getInventory: (date?: string) => Promise<ApiResponse<{ items: InventoryEntryItem[] }>>;
        getInventoryReport: (
            date?: string,
        ) => Promise<ApiResponse<{ date: string; grouped: InventorySummaryRow[] }>>;
        getInventoryDates: () => Promise<ApiResponse<{ dates: string[] }>>;
        getPurchaseCashPoints: () => Promise<ApiResponse<{ cashPoints: PurchaseCashPoint[] }>>;
        createPurchase: (
            request: PurchaseRequest,
        ) => Promise<ApiResponse<{ success: boolean; purchase: PurchaseResult }>>;
        listPurchases: (
            range: PurchaseListRange,
        ) => Promise<ApiResponse<{ purchases: PurchaseListItem[] }>>;
        addPurchaseToStock: (
            purchaseId: number,
        ) => Promise<
            ApiResponse<{
                success: boolean;
                purchase: { id: number; clientName: string; purchaseDate: string };
            }>
        >;
        createInventory: (data: Partial<Metal>) => Promise<ApiResponse<Metal>>;
        updateInventory: (id: string | number, data: Partial<Metal>) => Promise<ApiResponse<Metal>>;
        deleteInventory: (id: string | number) => Promise<ApiResponse<void>>;
        getStock: () => Promise<ApiResponse<StockResponse>>;
        getMetalStocks: () => Promise<ApiResponse<{ items: MetalStock[] }>>;
        addToInventory: (
            date: string,
        ) => Promise<ApiResponse<{ success: boolean; message: string; added: number }>>;
        getIncomingByMetal: () => Promise<ApiResponse<{ categories: MetalCategoryDates[] }>>;
        getIncomingByMetalItems: (
            date: string,
            metalType: MetalCategory,
        ) => Promise<ApiResponse<{ items: MetalInventoryItem[] }>>;
        addToInventoryByMetal: (
            selections: MetalDateSelection[],
        ) => Promise<ApiResponse<{ success: boolean; message: string; added: number }>>;
        reactivateContract: (
            contractNumber: string,
        ) => Promise<ApiResponse<{ success: boolean; message: string }>>;
        mergeInventoryDates: (
            dates: string[],
            newDate: string,
        ) => Promise<ApiResponse<{ success: boolean; message: string }>>;
        meltMetals: (
            meltingData: Array<{
                metalType: string;
                karat: number;
                quantityToMelt: number;
                pmp?: number;
            }>,
            name?: string,
            date?: string,
        ) => Promise<ApiResponse<{ success: boolean; message: string }>>;
        getMelts: () => Promise<ApiResponse<{ items: { id: number; date: string }[] }>>;
        getMeltDetails: (id: number) => Promise<
            ApiResponse<{
                melting: Record<string, unknown>;
                details: {
                    metalType: string;
                    karat: number;
                    quantityToMelt: number;
                    pmp: number;
                }[];
            }>
        >;
        updateMelt: (
            id: number,
            data: { afterMass: number; afterPurity: number },
        ) => Promise<ApiResponse<{ success: boolean; message?: string }>>;
        exportGoldAnnex: (date: string) => Promise<ApiResponse<Blob>>;
        exportElAltAnnex: (date: string) => Promise<ApiResponse<Blob>>;
        exportPostponements: () => Promise<ApiResponse<Blob>>;
        exportExpiredList: (fromDate: string, toDate: string) => Promise<ApiResponse<Blob>>;
        exportShortContracts: () => Promise<ApiResponse<Blob>>;
        exportExpiredObjects: (fromDate: string, toDate: string) => Promise<ApiResponse<Blob>>;
    };
    reports: {
        getDashboardStats: () => Promise<ApiResponse<DashboardStats>>;
        getDailyReport: (params: {
            date: string;
            sessionFilter?: boolean;
            cashPointId?: number | null;
        }) => Promise<ApiResponse<unknown>>;
        exportDailyReport: (params: {
            date: string;
            sessionFilter?: boolean;
            cashPointId?: number | null;
            format: string;
        }) => Promise<ApiResponse<Blob>>;
        getFinancialReport: (params?: ReportParams) => Promise<ApiResponse<FinancialReport>>;
        getClientReport: (params?: ReportParams) => Promise<ApiResponse<ClientReport[]>>;
        getInventoryReport: (params?: ReportParams) => Promise<ApiResponse<InventoryReport[]>>;
        exportReport: (type: string, params?: ReportParams) => Promise<ApiResponse<Blob>>;
    };
    payments: {
        getInvoicePdf: (invoiceNumber: string) => Promise<ApiResponse<Blob>>;
    };
    stats: {
        getOverview: () => Promise<ApiResponse<StatsOverview>>;
        getMonthly: (year: number, month: number) => Promise<ApiResponse<MonthlyStats>>;
        getYearly: (year: number) => Promise<ApiResponse<YearlyStats>>;
        getTopClients: (limit?: number) => Promise<ApiResponse<TopClient[]>>;
        getTopItems: (limit?: number) => Promise<ApiResponse<TopItem[]>>;
    };
    menu: {
        getAll: () => Promise<ApiResponse<MenuItem[]>>;
        getByRole: (role: string) => Promise<ApiResponse<MenuItem[]>>;
    };
    controlRoom: {
        getAll: (date?: string) => Promise<ApiResponse<ControlRoomData>>;
        getDailyBalance: (date?: string) => Promise<ApiResponse<DailyBalance[]>>;
        getDailyStats: (date?: string) => Promise<ApiResponse<DailyStats>>;
        getFinancialFlow: () => Promise<ApiResponse<FinancialFlow>>;
        getGrandTotals: () => Promise<ApiResponse<GrandTotals>>;
        getAu24kData: () => Promise<ApiResponse<Au24KData>>;
        getFinanceGroups: (date?: string) => Promise<ApiResponse<FinanceGroupData[]>>;
        updateFinanceGroupIndex: (
            id: number,
            indexBaza: number,
        ) => Promise<ApiResponse<{ success: boolean }>>;
        getAu24kBreakdown: (date?: string) => Promise<ApiResponse<Au24KBreakdownRow[]>>;
        getFinancialItems: (date?: string) => Promise<ApiResponse<FinancialItem[]>>;
        getFinanceNotes: () => Promise<ApiResponse<FinanceNote[]>>;
        addFinanceNote: (params: {
            denumire: string;
            suma: number;
        }) => Promise<ApiResponse<{ success: boolean; id: number | null }>>;
        deleteFinanceNote: (id: number) => Promise<ApiResponse<{ success: boolean }>>;
        getDeliveredNotInvoiced: () => Promise<ApiResponse<DeliveredNotInvoiced[]>>;
        deleteDeliveredNotInvoiced: (id: number) => Promise<ApiResponse<{ success: boolean }>>;
        updateFinancialItemSummed: (
            id: number,
            isSumed: boolean,
        ) => Promise<ApiResponse<{ success: boolean }>>;
        updateFinancialItemOrder: (
            orderUpdates: Array<{ id: number; ordern: number }>,
        ) => Promise<ApiResponse<{ success: boolean }>>;
        addFinancialItem: (params: {
            denumire: string;
            suma: number;
            reprezentand: string;
        }) => Promise<ApiResponse<{ success: boolean; id: number }>>;
        modifyFinancialItem: (
            id: number,
            params: { delta: number; reprezentand: string },
        ) => Promise<ApiResponse<{ success: boolean }>>;
        deleteFinancialItem: (id: number) => Promise<ApiResponse<{ success: boolean }>>;
        getDailyMovements: (
            date?: string,
            cashPointId?: number,
        ) => Promise<ApiResponse<DailyMovementsGroup[]>>;
        getDailyCashCheck: (
            date?: string,
            cashPointId?: number,
        ) => Promise<ApiResponse<DailyCashCheck>>;
        getAuditLog: (
            filters: AuditLogFilters,
            page: number,
            pageSize: number,
            upToId?: number | null,
        ) => Promise<ApiResponse<AuditLogListItem[]> & { pagination?: AuditLogPagination }>;
        getAuditLogEntry: (id: number) => Promise<ApiResponse<AuditLogEntry>>;
        getAuditLogCatalogue: () => Promise<ApiResponse<AuditLogCatalogue>>;
    };
    configuration: {
        getAll: () => Promise<ApiResponse<Record<string, string>>>;
        get: (denumire: string) => Promise<ApiResponse<{ denumire: string; valoare: string }>>;
        update: (
            denumire: string,
            valoare: string,
        ) => Promise<ApiResponse<{ denumire: string; valoare: string }>>;
        updateBatch: (
            configurations: Array<{ denumire: string; valoare: string }>,
        ) => Promise<ApiResponse<Array<{ denumire: string; valoare: string }>>>;
        sendSmtpTest: (to: string) => Promise<ApiResponse<void>>;
        /**
         * The number the next invoice and receipt will get, from the `nrfactint` sequence both
         * programs draw from. Read-only: nothing is reserved, and no setting changes it.
         */
        getNextInvoiceNumber: () => Promise<ApiResponse<{ nextInvoiceNumber: string }>>;
        /**
         * Label printer settings for one cashpoint.
         *
         * Deliberately not routed through `getAll`/`updateBatch`: those address a key by name
         * alone, which collapses 21 per-cashpoint rows into one and writes back to whichever
         * the database returned first.
         */
        getLabelPrinter: (cashPointId: number) => Promise<ApiResponse<LabelPrinterConfigPayload>>;
        saveLabelPrinter: (
            cashPointId: number,
            body: { queue: string; stockId: string | null },
        ) => Promise<ApiResponse<LabelPrinterConfigPayload>>;
        /**
         * The till alert and overpaid tolerances of one cash point - per cash point for the same
         * reason as the label printer: Delphi keeps 20 rows of each.
         */
        getCashPointAlerts: (
            cashPointId: number,
        ) => Promise<ApiResponse<CashPointAlertSettingsPayload>>;
        saveCashPointAlerts: (
            cashPointId: number,
            body: CashPointAlertSettingsPayload,
        ) => Promise<ApiResponse<CashPointAlertSettingsPayload>>;
        /**
         * The loyalty categories (Delphi's `bonus` table) and the standard discount. Not a set of
         * `system_configuration` names, so not part of `getAll`/`updateBatch`.
         */
        getLoyalty: () => Promise<ApiResponse<LoyaltySettingsPayload>>;
        saveLoyalty: (body: LoyaltySettingsPayload) => Promise<ApiResponse<LoyaltySettingsPayload>>;
    };
    invoiceDocument: {
        getSettings: () => Promise<
            ApiResponse<{
                engine: InvoiceDocumentEngine;
                availableEngines: InvoiceDocumentEngine[];
                supportedEngines: InvoiceDocumentEngine[];
            }>
        >;
        updateSettings: (
            engine: InvoiceDocumentEngine,
        ) => Promise<ApiResponse<{ engine: InvoiceDocumentEngine }>>;
        getActiveTemplate: () => Promise<ApiResponse<{ template: InvoiceDocumentTemplatePayload }>>;
        listTemplates: () => Promise<ApiResponse<{ templates: InvoiceDocumentTemplatePayload[] }>>;
        getTemplate: (
            templateId: number,
        ) => Promise<ApiResponse<{ template: InvoiceDocumentTemplatePayload }>>;
        saveDraft: (
            templateId: number,
            input: { name?: string; htmlContent?: string; cssContent?: string | null },
        ) => Promise<ApiResponse<{ template: InvoiceDocumentTemplatePayload }>>;
        duplicate: (
            templateId: number,
            input: { name?: string; htmlContent?: string; cssContent?: string | null },
        ) => Promise<ApiResponse<{ template: InvoiceDocumentTemplatePayload }>>;
        publish: (
            templateId: number,
        ) => Promise<ApiResponse<{ template: InvoiceDocumentTemplatePayload }>>;
        restoreDefault: (
            templateId: number,
            defaults: { defaultHtmlContent: string; defaultCssContent?: string | null },
        ) => Promise<ApiResponse<{ template: InvoiceDocumentTemplatePayload }>>;
        getFactoryDefault: () => Promise<
            ApiResponse<{ name: string; htmlContent: string; cssContent: string | null }>
        >;
        previewByInvoiceNumber: (
            invoiceNumber: string,
            templateId?: number,
            documentType?: string,
        ) => Promise<ApiResponse<Blob>>;
    };
    contractDocument: {
        getSettings: () => Promise<
            ApiResponse<{
                engine: ContractDocumentEngine;
                availableEngines: ContractDocumentEngine[];
                supportedEngines: ContractDocumentEngine[];
            }>
        >;
        updateSettings: (
            engine: ContractDocumentEngine,
        ) => Promise<ApiResponse<{ engine: ContractDocumentEngine }>>;
        getActiveTemplate: () => Promise<
            ApiResponse<{
                template: ContractDocumentTemplatePayload;
                assets: ContractDocumentAssetPayload[];
            }>
        >;
        listTemplates: () => Promise<ApiResponse<{ templates: ContractDocumentTemplatePayload[] }>>;
        getTemplate: (templateId: number) => Promise<
            ApiResponse<{
                template: ContractDocumentTemplatePayload;
                assets: ContractDocumentAssetPayload[];
            }>
        >;
        saveDraft: (
            templateId: number,
            input: { name?: string; htmlContent?: string; cssContent?: string | null },
        ) => Promise<ApiResponse<{ template: ContractDocumentTemplatePayload }>>;
        duplicate: (
            templateId: number,
            input: { name?: string; htmlContent?: string; cssContent?: string | null },
        ) => Promise<
            ApiResponse<{
                template: ContractDocumentTemplatePayload;
                assets: ContractDocumentAssetPayload[];
            }>
        >;
        publish: (
            templateId: number,
        ) => Promise<ApiResponse<{ template: ContractDocumentTemplatePayload }>>;
        restoreDefault: (
            templateId: number,
            defaults: { defaultHtmlContent: string; defaultCssContent?: string | null },
        ) => Promise<ApiResponse<{ template: ContractDocumentTemplatePayload }>>;
        getFactoryDefault: () => Promise<
            ApiResponse<{ name: string; htmlContent: string; cssContent: string | null }>
        >;
        listAssets: (
            templateId: number,
        ) => Promise<ApiResponse<{ assets: ContractDocumentAssetPayload[] }>>;
        uploadAsset: (
            templateId: number,
            input: {
                assetType: 'logo' | 'page_background';
                pageNumber?: number | null;
                mimeType: string;
                contentBase64: string;
            },
        ) => Promise<ApiResponse<{ asset: ContractDocumentAssetPayload }>>;
        deleteAsset: (templateId: number, assetId: number) => Promise<ApiResponse<void>>;
        previewByNr: (nr: number) => Promise<ApiResponse<Blob>>;
    };
    statuts: {
        execute: () => Promise<ApiResponse<StatutsExecutionResult>>;
        getStatus: () => Promise<ApiResponse<StatutsStatus>>;
    };
    statistics: {
        getData: (params?: {
            startDate?: string;
            endDate?: string;
            timeframe?: StatisticsTimeframe;
            currency?: 'EUR' | 'LEI';
        }) => Promise<ApiResponse<BackendStatisticsResponse>>;
        getAverages: () => Promise<
            ApiResponse<{ success: boolean; data: Array<{ series: string; value: number }> }>
        >;
        updateAverage: (
            series: string,
            value: number,
        ) => Promise<ApiResponse<{ success: boolean; message: string }>>;
        getBusinessIntelligence: (params?: {
            startDate?: string;
            endDate?: string;
            timeframe?: StatisticsTimeframe;
            currency?: 'EUR' | 'LEI';
        }) => Promise<ApiResponse<BusinessIntelligenceResponse>>;
        getAdvancedAnalytics: (params?: {
            startDate?: string;
            endDate?: string;
            currency?: 'EUR' | 'LEI';
        }) => Promise<ApiResponse<AdvancedAnalyticsResponse>>;
        getClientStatistics: (params?: {
            startDate?: string;
            endDate?: string;
        }) => Promise<ApiResponse<ClientStatisticsResponse>>;
        getOperationsData: (params?: {
            startDate?: string;
            endDate?: string;
        }) => Promise<ApiResponse<OperationsResponse>>;
        getInventoryTrends: (params?: {
            startDate?: string;
            endDate?: string;
            currency?: 'EUR' | 'LEI';
        }) => Promise<ApiResponse<InventoryTrendsResponse>>;
        getExpensesData: (params?: {
            startDate?: string;
            endDate?: string;
        }) => Promise<ApiResponse<ExpensesResponse>>;
        getGoodsSalesData: (params?: {
            startDate?: string;
            endDate?: string;
            currency?: 'EUR' | 'LEI';
        }) => Promise<ApiResponse<GoodsSalesResponse>>;
        getGoldValueData: (params?: {
            startDate?: string;
            endDate?: string;
            timeframe?: StatisticsTimeframe;
        }) => Promise<ApiResponse<GoldValueResponse>>;
        getGoldPriceSimulation: (params?: {
            currency?: 'EUR' | 'LEI';
        }) => Promise<ApiResponse<GoldPriceChangeSimulationResponse>>;
        getCashDemandProjection: (params?: {
            priceChangePercent?: number;
            monthsAhead?: number;
            currency?: 'EUR' | 'LEI';
        }) => Promise<ApiResponse<CashDemandProjectionResponse>>;
        /**
         * Bring the statistics snapshots up to date. Without `force` the server takes them only
         * when the last complete refresh is a few minutes old or from an earlier day.
         */
        refresh: (options?: { force?: boolean }) => Promise<ApiResponse<StatisticsRefreshResponse>>;
    };
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const useApi = (): ApiContextType => {
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error('useApi must be used within an ApiProvider');
    }
    return context;
};

interface ApiProviderProps {
    children: ReactNode;
}

export const ApiProvider = ({ children }: ApiProviderProps) => {
    const { token, API_BASE_URL, refreshToken, logout } = useAuth();

    const value: ApiContextType = useMemo(() => {
        const makeRequest = async <T = unknown,>(
            endpoint: string,
            options: RequestOptions = {},
        ): Promise<ApiResponse<T>> => {
            const url = `${API_BASE_URL}${endpoint}`;
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...options.headers,
                },
                ...options,
            };

            try {
                let response = await fetch(url, config);

                // Handle token refresh for 401 errors
                if (response.status === 401 && token) {
                    const refreshResult = await refreshToken();
                    if (refreshResult.success) {
                        // Retry the request with new token
                        config.headers['Authorization'] = `Bearer ${refreshResult.token}`;
                        response = await fetch(url, config);
                    } else {
                        logout();
                        throw new Error('Authentication failed');
                    }
                }

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(apiErrorMessage(data, response.status, translateKey));
                }

                return { success: true, data };
            } catch (error) {
                console.error('API request error:', error);
                return { success: false, error: (error as Error).message };
            }
        };

        /** Shared helper to fetch document settings (contract-document / invoice-document). */
        const fetchDocumentSettings = async <T,>(endpoint: string): Promise<ApiResponse<T>> => {
            const res = await makeRequest<T>(endpoint);
            if (!res.success || !res.data) {
                return { success: false, error: res.error ?? 'No data' };
            }
            return { success: true, data: res.data };
        };

        /** Shared helper to update document settings (contract-document / invoice-document). */
        const updateDocumentSettings = async <T,>(
            endpoint: string,
            engine: string,
        ): Promise<ApiResponse<T>> => {
            const res = await makeRequest<T>(endpoint, {
                method: 'PUT',
                body: JSON.stringify({ engine }),
            });
            if (!res.success || !res.data) {
                return { success: false, error: res.error ?? 'No data' };
            }
            return { success: true, data: res.data };
        };

        /** Shared helper to fetch a binary blob (PDF/Excel) via a direct fetch call. */
        const fetchBlob = async (url: string, accept: string): Promise<ApiResponse<Blob>> => {
            try {
                const response = await fetch(url, {
                    headers: {
                        ...(token && { Authorization: `Bearer ${token}` }),
                        Accept: accept,
                    },
                });
                if (!response.ok) {
                    const body = await response.json().catch(() => ({}));
                    return {
                        success: false,
                        error: apiErrorMessage(body, response.status, translateKey),
                    };
                }
                return { success: true, data: await response.blob() };
            } catch (error) {
                return { success: false, error: (error as Error).message };
            }
        };

        /** Shared helper to fetch a PDF blob via a direct fetch call. */
        const fetchPdfBlob = (url: string): Promise<ApiResponse<Blob>> =>
            fetchBlob(url, 'application/pdf');

        /** Shared helper to fetch an Excel (.xlsx) blob via a direct fetch call. */
        const fetchExcelBlob = (url: string): Promise<ApiResponse<Blob>> =>
            fetchBlob(url, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

        // Inventory API methods
        const inventoryApi = {
            getAll: async (params: QueryParams = {}) => {
                const queryString = new URLSearchParams(
                    params as Record<string, string>,
                ).toString();
                const endpoint = queryString ? `/inventory?${queryString}` : '/inventory';
                return makeRequest<{ items: InventoryItem[]; pagination: Pagination }>(endpoint);
            },

            getById: async (id: string | number) => {
                return makeRequest<InventoryItem>(`/inventory/${id}`);
            },

            create: async (itemData: Partial<InventoryItem>) => {
                return makeRequest<InventoryItem>('/inventory', {
                    method: 'POST',
                    body: JSON.stringify(itemData),
                });
            },

            update: async (id: string | number, itemData: Partial<InventoryItem>) => {
                return makeRequest<InventoryItem>(`/inventory/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(itemData),
                });
            },

            delete: async (id: string | number) => {
                return makeRequest<void>(`/inventory/${id}`, {
                    method: 'DELETE',
                });
            },

            search: async (term: string) => {
                return makeRequest<InventoryItem[]>(
                    `/inventory/search/${encodeURIComponent(term)}`,
                );
            },
        };

        // Metals API methods
        const metalsApi = {
            getAll: async (params: QueryParams = {}) => {
                const queryString = new URLSearchParams(
                    params as Record<string, string>,
                ).toString();
                const endpoint = queryString ? `/metals?${queryString}` : '/metals';
                return makeRequest<Metal[]>(endpoint);
            },

            getIncoming: async (date?: string) => {
                const query = date ? `?date=${encodeURIComponent(date)}` : '';
                return makeRequest<{ date: string; metals: IncomingMetal[] }>(
                    `/metals/incoming${query}`,
                );
            },

            getIncomingDates: async () => {
                return makeRequest<{ dates: string[] }>('/metals/incoming/dates');
            },

            getIncomingReport: async (date?: string) => {
                const query = date ? `?date=${encodeURIComponent(date)}` : '';
                return makeRequest<IncomingMetalReport>(`/metals/incoming/report${query}`);
            },

            getById: async (id: string | number) => {
                return makeRequest<Metal>(`/metals/${id}`);
            },

            update: async (id: string | number, data: Partial<Metal>) => {
                return makeRequest<Metal>(`/metals/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                });
            },

            delete: async (id: string | number) => {
                return makeRequest<void>(`/metals/${id}`, {
                    method: 'DELETE',
                });
            },

            // Inventory entry methods
            getInventory: async (date?: string) => {
                const query = date ? `?date=${encodeURIComponent(date)}` : '';
                return makeRequest<{ items: InventoryEntryItem[] }>(`/metals/inventory${query}`);
            },

            getInventoryReport: async (date?: string) => {
                const query = date ? `?date=${encodeURIComponent(date)}` : '';
                return makeRequest<{ date: string; grouped: InventorySummaryRow[] }>(
                    `/metals/inventory/report${query}`,
                );
            },

            getInventoryDates: async () => {
                return makeRequest<{ dates: string[] }>('/metals/inventory/dates');
            },

            getPurchaseCashPoints: async () => {
                return makeRequest<{ cashPoints: PurchaseCashPoint[] }>(
                    '/metals/purchases/cash-points',
                );
            },

            createPurchase: async (request: PurchaseRequest) => {
                return makeRequest<{ success: boolean; purchase: PurchaseResult }>(
                    '/metals/purchases',
                    { method: 'POST', body: JSON.stringify(request) },
                );
            },

            listPurchases: async (range: PurchaseListRange) => {
                return makeRequest<{ purchases: PurchaseListItem[] }>(
                    `/metals/purchases${purchaseListQueryString(range)}`,
                );
            },

            addPurchaseToStock: async (purchaseId: number) => {
                return makeRequest<{
                    success: boolean;
                    purchase: { id: number; clientName: string; purchaseDate: string };
                }>(`/metals/purchases/${purchaseId}/add-to-stock`, { method: 'POST' });
            },

            createInventory: async (data: Partial<Metal>) => {
                return makeRequest<Metal>('/metals/inventory', {
                    method: 'POST',
                    body: JSON.stringify(data),
                });
            },

            updateInventory: async (id: string | number, data: Partial<Metal>) => {
                return makeRequest<Metal>(`/metals/inventory/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                });
            },

            deleteInventory: async (id: string | number) => {
                return makeRequest<void>(`/metals/inventory/${id}`, {
                    method: 'DELETE',
                });
            },

            getStock: async () => {
                return makeRequest<StockResponse>('/metals/stock');
            },

            getMetalStocks: async () => {
                return makeRequest<{ items: MetalStock[] }>('/metals/metal-stocks');
            },

            addToInventory: async (date: string) => {
                return makeRequest<{ success: boolean; message: string; added: number }>(
                    '/metals/inventory/bulk-add',
                    {
                        method: 'POST',
                        body: JSON.stringify({ date }),
                    },
                );
            },

            getIncomingByMetal: async () => {
                return makeRequest<{ categories: MetalCategoryDates[] }>(
                    '/metals/incoming/by-metal',
                );
            },

            getIncomingByMetalItems: async (date: string, metalType: MetalCategory) => {
                const query = new URLSearchParams({ date, metalType }).toString();
                return makeRequest<{ items: MetalInventoryItem[] }>(
                    `/metals/incoming/by-metal/items?${query}`,
                );
            },

            addToInventoryByMetal: async (selections: MetalDateSelection[]) => {
                return makeRequest<{ success: boolean; message: string; added: number }>(
                    '/metals/inventory/bulk-add-by-metal',
                    {
                        method: 'POST',
                        body: JSON.stringify({ selections }),
                    },
                );
            },

            reactivateContract: async (contractNumber: string) => {
                return makeRequest<{ success: boolean; message: string }>(
                    `/metals/reactivate/${encodeURIComponent(contractNumber)}`,
                    {
                        method: 'POST',
                    },
                );
            },
            mergeInventoryDates: async (dates: string[], newDate: string) => {
                return makeRequest<{ success: boolean; message: string }>(
                    '/metals/inventory/merge',
                    {
                        method: 'POST',
                        body: JSON.stringify({ dates, newDate }),
                    },
                );
            },

            meltMetals: async (
                meltingData: Array<{
                    metalType: string;
                    karat: number;
                    quantityToMelt: number;
                    pmp?: number;
                }>,
                name?: string,
                date?: string,
            ) => {
                return makeRequest<{ success: boolean; message: string }>('/metals/melt', {
                    method: 'POST',
                    body: JSON.stringify({ meltingData, name, date }),
                });
            },
            getMelts: async () => {
                return makeRequest<{ items: Array<{ id: number; date: string }> }>('/metals/melts');
            },
            getMeltDetails: async (id: number) => {
                return makeRequest<{
                    melting: Record<string, unknown>;
                    details: Array<{
                        metalType: string;
                        karat: number;
                        quantityToMelt: number;
                        pmp: number;
                    }>;
                }>(`/metals/melts/${id}`);
            },
            updateMelt: async (id: number, data: { afterMass: number; afterPurity: number }) => {
                return makeRequest<{ success: boolean; message?: string }>(`/metals/melts/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                });
            },
            exportGoldAnnex: async (date: string) => {
                const url = `${API_BASE_URL}/metals/annex-gold/export?date=${encodeURIComponent(date)}&lang=${encodeURIComponent(i18n.language)}`;
                return fetchExcelBlob(url);
            },
            exportElAltAnnex: async (date: string) => {
                const url = `${API_BASE_URL}/metals/annex-elal/export?date=${encodeURIComponent(date)}&lang=${encodeURIComponent(i18n.language)}`;
                return fetchExcelBlob(url);
            },
            exportPostponements: async () => {
                const url = `${API_BASE_URL}/metals/postponements/export?lang=${encodeURIComponent(i18n.language)}`;
                return fetchExcelBlob(url);
            },
            exportExpiredList: async (fromDate: string, toDate: string) => {
                const url = `${API_BASE_URL}/metals/expired-list/export?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}&lang=${encodeURIComponent(i18n.language)}`;
                return fetchExcelBlob(url);
            },
            exportShortContracts: async () => {
                const url = `${API_BASE_URL}/metals/short-contracts/export?lang=${encodeURIComponent(i18n.language)}`;
                return fetchExcelBlob(url);
            },
            exportExpiredObjects: async (fromDate: string, toDate: string) => {
                const url = `${API_BASE_URL}/metals/expired-objects/export?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}&lang=${encodeURIComponent(i18n.language)}`;
                return fetchExcelBlob(url);
            },
        };

        // Reports API methods
        const reportsApi = {
            getDashboardStats: async () => {
                return makeRequest<DashboardStats>('/reports/dashboard');
            },

            getDailyReport: async (params: {
                date: string;
                sessionFilter?: boolean;
                cashPointId?: number | null;
            }) => {
                const queryParams = new URLSearchParams();
                queryParams.append('date', params.date);
                appendDailyReportSessionFilter(queryParams, params);
                return makeRequest<unknown>(`/reports/daily-full?${queryParams.toString()}`);
            },

            exportDailyReport: async (params: {
                date: string;
                sessionFilter?: boolean;
                cashPointId?: number | null;
                format: string;
            }) => {
                const queryParams = new URLSearchParams();
                queryParams.append('date', params.date);
                appendDailyReportSessionFilter(queryParams, params);
                queryParams.append('format', params.format);
                queryParams.append('lang', i18n.language);
                const url = `${API_BASE_URL}/reports/daily-full/export?${queryParams.toString()}`;
                try {
                    const response = await fetch(url, {
                        headers: {
                            ...(token && { Authorization: `Bearer ${token}` }),
                            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        },
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const blob = await response.blob();
                    return { success: true, data: blob };
                } catch (error) {
                    console.error('Export daily report error:', error);
                    return { success: false, error: (error as Error).message };
                }
            },

            getFinancialReport: async (params: ReportParams = {}) => {
                const queryString = new URLSearchParams(
                    params as Record<string, string>,
                ).toString();
                const endpoint = queryString
                    ? `/reports/financial?${queryString}`
                    : '/reports/financial';
                return makeRequest<FinancialReport>(endpoint);
            },

            getClientReport: async (params: ReportParams = {}) => {
                const queryString = new URLSearchParams(
                    params as Record<string, string>,
                ).toString();
                const endpoint = queryString
                    ? `/reports/clients?${queryString}`
                    : '/reports/clients';
                return makeRequest<ClientReport[]>(endpoint);
            },

            getInventoryReport: async (params: ReportParams = {}) => {
                const queryString = new URLSearchParams(
                    params as Record<string, string>,
                ).toString();
                const endpoint = queryString
                    ? `/reports/inventory?${queryString}`
                    : '/reports/inventory';
                return makeRequest<InventoryReport[]>(endpoint);
            },

            exportReport: async (type: string, params: ReportParams = {}) => {
                const queryString = new URLSearchParams({
                    ...(params as Record<string, string>),
                    lang: i18n.language,
                }).toString();
                const endpoint = `/reports/export/${type}?${queryString}`;
                return makeRequest<Blob>(endpoint, {
                    headers: {
                        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    },
                });
            },
        };

        const paymentsApi: ApiContextType['payments'] = {
            getInvoicePdf: async invoiceNumber => {
                const url = `${API_BASE_URL}/payments/invoice/${encodeURIComponent(invoiceNumber)}`;
                return fetchPdfBlob(url);
            },
        };

        // Stats API methods
        const statsApi = {
            getOverview: async () => {
                return makeRequest<StatsOverview>('/stats/overview');
            },

            getMonthly: async (year: number, month: number) => {
                return makeRequest<MonthlyStats>(`/stats/monthly/${year}/${month}`);
            },

            getYearly: async (year: number) => {
                return makeRequest<YearlyStats>(`/stats/yearly/${year}`);
            },

            getTopClients: async (limit = 10) => {
                return makeRequest<TopClient[]>(`/stats/top-clients?limit=${limit}`);
            },

            getTopItems: async (limit = 10) => {
                return makeRequest<TopItem[]>(`/stats/top-items?limit=${limit}`);
            },
        };

        // Menu API methods (for dynamic menu items)
        const menuApi = {
            getAll: async () => {
                return makeRequest<MenuItem[]>('/menu');
            },

            getByRole: async (role: string) => {
                return makeRequest<MenuItem[]>(`/menu/role/${role}`);
            },
        };

        // Control Room API methods
        const controlRoomApi = {
            getAll: async (date?: string) => {
                const query = date ? `?date=${encodeURIComponent(date)}` : '';
                return makeRequest<ControlRoomData>(`/control-room/all${query}`);
            },

            getDailyBalance: async (date?: string) => {
                const query = date ? `?date=${encodeURIComponent(date)}` : '';
                return makeRequest<DailyBalance[]>(`/control-room/daily-balance${query}`);
            },

            getDailyStats: async (date?: string) => {
                const query = date ? `?date=${encodeURIComponent(date)}` : '';
                return makeRequest<DailyStats>(`/control-room/daily-stats${query}`);
            },

            getFinancialFlow: async () => {
                return makeRequest<FinancialFlow>('/control-room/financial-flow');
            },

            getGrandTotals: async () => {
                return makeRequest<GrandTotals>('/control-room/grand-totals');
            },

            getAu24kData: async () => {
                return makeRequest<Au24KData>('/control-room/au24k-data');
            },

            getFinanceGroups: async (date?: string) => {
                const query = date ? `?date=${date}` : '';
                return makeRequest<FinanceGroupData[]>(`/control-room/finance-groups${query}`);
            },

            updateFinanceGroupIndex: async (id: number, indexBaza: number) => {
                return makeRequest<{ success: boolean }>(`/control-room/finance-groups/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ indexBaza }),
                });
            },

            getAu24kBreakdown: async (date?: string) => {
                const query = date ? `?date=${date}` : '';
                return makeRequest<Au24KBreakdownRow[]>(`/control-room/au24k-breakdown${query}`);
            },

            getFinancialItems: async (date?: string) => {
                const query = date ? `?date=${encodeURIComponent(date)}` : '';
                return makeRequest<FinancialItem[]>(`/control-room/financial-items${query}`);
            },

            getFinanceNotes: async () => {
                return makeRequest<FinanceNote[]>('/control-room/finance-notes');
            },

            addFinanceNote: async (params: { denumire: string; suma: number }) => {
                return makeRequest<{ success: boolean; id: number | null }>(
                    '/control-room/finance-notes',
                    {
                        method: 'POST',
                        body: JSON.stringify(params),
                    },
                );
            },

            deleteFinanceNote: async (id: number) => {
                return makeRequest<{ success: boolean }>(`/control-room/finance-notes/${id}`, {
                    method: 'DELETE',
                });
            },

            getDeliveredNotInvoiced: async () => {
                return makeRequest<DeliveredNotInvoiced[]>('/control-room/delivered-not-invoiced');
            },

            deleteDeliveredNotInvoiced: async (id: number) => {
                return makeRequest<{ success: boolean }>(
                    `/control-room/delivered-not-invoiced/${id}`,
                    { method: 'DELETE' },
                );
            },

            updateFinancialItemSummed: async (id: number, isSumed: boolean) => {
                return makeRequest<{ success: boolean }>(
                    `/control-room/financial-items/${id}/summed`,
                    {
                        method: 'PUT',
                        body: JSON.stringify({ isSumed }),
                    },
                );
            },

            updateFinancialItemOrder: async (
                orderUpdates: Array<{ id: number; ordern: number }>,
            ) => {
                return makeRequest<{ success: boolean }>('/control-room/financial-items/order', {
                    method: 'PUT',
                    body: JSON.stringify({ orderUpdates }),
                });
            },

            addFinancialItem: async (params: {
                denumire: string;
                suma: number;
                reprezentand: string;
            }) => {
                return makeRequest<{ success: boolean; id: number }>(
                    '/control-room/financial-items',
                    {
                        method: 'POST',
                        body: JSON.stringify(params),
                    },
                );
            },

            modifyFinancialItem: async (
                id: number,
                params: { delta: number; reprezentand: string },
            ) => {
                return makeRequest<{ success: boolean }>(
                    `/control-room/financial-items/${id}/modify`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(params),
                    },
                );
            },

            deleteFinancialItem: async (id: number) => {
                return makeRequest<{ success: boolean }>(`/control-room/financial-items/${id}`, {
                    method: 'DELETE',
                });
            },

            getDailyMovements: async (date?: string, cashPointId?: number) => {
                const params = new URLSearchParams();
                if (date) params.set('date', date);
                if (cashPointId !== undefined) params.set('cashPointId', String(cashPointId));
                const query = params.size > 0 ? `?${params.toString()}` : '';
                return makeRequest<DailyMovementsGroup[]>(`/control-room/daily-movements${query}`);
            },

            getDailyCashCheck: async (date?: string, cashPointId?: number) => {
                const params = new URLSearchParams();
                if (date) params.set('date', date);
                if (cashPointId !== undefined) params.set('cashPointId', String(cashPointId));
                const query = params.size > 0 ? `?${params.toString()}` : '';
                return makeRequest<DailyCashCheck>(`/control-room/daily-cash-check${query}`);
            },

            getAuditLog: async (
                filters: AuditLogFilters,
                page: number,
                pageSize: number,
                upToId?: number | null,
            ) => {
                const query = buildAuditLogSearchParams(filters, page, pageSize, upToId);
                const response = await makeRequest<{
                    success: boolean;
                    data: AuditLogListItem[];
                    pagination: AuditLogPagination;
                }>(`/control-room/audit-log?${query}`);
                if (!response.success || !response.data?.success) {
                    return { success: false, error: response.error ?? 'Failed to get audit log' };
                }
                return {
                    success: true,
                    data: response.data.data,
                    pagination: response.data.pagination,
                };
            },
            getAuditLogEntry: async (id: number) =>
                unwrapAuditLogResponse(
                    await makeRequest<AuditLogEnvelope<AuditLogEntry>>(
                        `/control-room/audit-log/entries/${id}`,
                    ),
                    'Failed to get audit log entry',
                ),
            getAuditLogCatalogue: async () =>
                unwrapAuditLogResponse(
                    await makeRequest<AuditLogEnvelope<AuditLogCatalogue>>(
                        '/control-room/audit-log/catalogue',
                    ),
                    'Failed to get audit log catalogue',
                ),
        };

        // Statuts API methods
        const statutsApi = {
            execute: async () => {
                return makeRequest<StatutsExecutionResult>('/statuts/execute', {
                    method: 'POST',
                });
            },

            getStatus: async () => {
                return makeRequest<StatutsStatus>('/statuts/status');
            },
        };

        // Statistics API methods
        const statisticsApi = {
            getData: async (params?: {
                startDate?: string;
                endDate?: string;
                timeframe?: StatisticsTimeframe;
                currency?: 'EUR' | 'LEI';
            }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const endpoint = query ? `/statistics/data?${query}` : '/statistics/data';
                // Unwrap backend envelope { success, data } so consumers receive the payload directly
                type BackendEnvelope = { success: boolean; data?: BackendStatisticsResponse };
                type ResponseUnion = BackendEnvelope | BackendStatisticsResponse;
                const res = await makeRequest<ResponseUnion>(endpoint);
                if (!res.success) {
                    return {
                        success: false,
                        error: res.error ?? 'Failed to fetch statistics data',
                    };
                }
                const backend = res.data;
                const isBackendStatisticsResponse = (
                    obj: unknown,
                ): obj is BackendStatisticsResponse => {
                    return (
                        typeof obj === 'object' &&
                        obj !== null &&
                        Array.isArray((obj as { dailyRevenue?: unknown }).dailyRevenue)
                    );
                };
                // Case 1: normal envelope { success, data }
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: BackendStatisticsResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                // Case 2: payload returned directly (fallback)
                if (isBackendStatisticsResponse(backend)) {
                    return { success: true, data: backend };
                }
                return { success: false, error: 'Invalid statistics response format' };
            },

            getAverages: async () => {
                return makeRequest<{
                    success: boolean;
                    data: Array<{ series: string; value: number }>;
                }>('/statistics/averages');
            },

            updateAverage: async (series: string, value: number) => {
                return makeRequest<{ success: boolean; message: string }>(
                    `/statistics/averages/${series}`,
                    {
                        method: 'PUT',
                        body: JSON.stringify({ value }),
                    },
                );
            },

            getBusinessIntelligence: async (params?: {
                startDate?: string;
                endDate?: string;
                timeframe?: StatisticsTimeframe;
                currency?: 'EUR' | 'LEI';
            }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const endpoint = query
                    ? `/statistics/business-intelligence?${query}`
                    : '/statistics/business-intelligence';
                type BackendEnvelope = { success: boolean; data?: BusinessIntelligenceResponse };
                type ResponseUnion = BackendEnvelope | BusinessIntelligenceResponse;
                const res = await makeRequest<ResponseUnion>(endpoint);
                if (!res.success) {
                    return {
                        success: false,
                        error: res.error ?? 'Failed to fetch business intelligence data',
                    };
                }
                const backend = res.data;
                // Case 1: normal envelope { success, data }
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: BusinessIntelligenceResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                // Case 2: payload returned directly (fallback)
                const isBusinessIntelligenceResponse = (
                    obj: unknown,
                ): obj is BusinessIntelligenceResponse => {
                    return (
                        typeof obj === 'object' &&
                        obj !== null &&
                        'priceSimulations' in obj &&
                        Array.isArray((obj as { priceSimulations?: unknown }).priceSimulations)
                    );
                };
                if (isBusinessIntelligenceResponse(backend)) {
                    return { success: true, data: backend };
                }
                return { success: false, error: 'Invalid business intelligence response format' };
            },

            getAdvancedAnalytics: async (params?: {
                startDate?: string;
                endDate?: string;
                currency?: 'EUR' | 'LEI';
            }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const endpoint = query
                    ? `/statistics/advanced-analytics?${query}`
                    : '/statistics/advanced-analytics';
                type BackendEnvelope = { success: boolean; data?: AdvancedAnalyticsResponse };
                type ResponseUnion = BackendEnvelope | AdvancedAnalyticsResponse;
                const res = await makeRequest<ResponseUnion>(endpoint);
                if (!res.success) {
                    return {
                        success: false,
                        error: res.error ?? 'Failed to fetch advanced analytics data',
                    };
                }
                const backend = res.data;
                // Case 1: normal envelope { success, data }
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: AdvancedAnalyticsResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                // Case 2: payload returned directly (fallback)
                const isAdvancedAnalyticsResponse = (
                    obj: unknown,
                ): obj is AdvancedAnalyticsResponse => {
                    return (
                        typeof obj === 'object' &&
                        obj !== null &&
                        'seasonality' in obj &&
                        Array.isArray((obj as { seasonality?: unknown }).seasonality)
                    );
                };
                if (isAdvancedAnalyticsResponse(backend)) {
                    return { success: true, data: backend };
                }
                return { success: false, error: 'Invalid advanced analytics response format' };
            },

            getClientStatistics: async (params?: { startDate?: string; endDate?: string }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const endpoint = query ? `/statistics/clients?${query}` : '/statistics/clients';
                type BackendEnvelope = { success: boolean; data?: ClientStatisticsResponse };
                type ResponseUnion = BackendEnvelope | ClientStatisticsResponse;
                const response = await makeRequest<ResponseUnion>(endpoint);
                if (!response.success) {
                    return {
                        success: false,
                        error: response.error ?? 'Failed to fetch client statistics',
                    };
                }
                const backend = response.data;
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: ClientStatisticsResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                const isClientStatisticsResponse = (
                    value: unknown,
                ): value is ClientStatisticsResponse => {
                    return (
                        typeof value === 'object' &&
                        value !== null &&
                        'newClients' in value &&
                        Array.isArray((value as { newClients?: unknown }).newClients)
                    );
                };
                if (isClientStatisticsResponse(backend)) {
                    return { success: true, data: backend };
                }
                return { success: false, error: 'Invalid client statistics response format' };
            },

            getOperationsData: async (params?: { startDate?: string; endDate?: string }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const endpoint = query
                    ? `/statistics/operations?${query}`
                    : '/statistics/operations';
                type BackendEnvelope = { success: boolean; data?: OperationsResponse };
                type ResponseUnion = BackendEnvelope | OperationsResponse;
                const res = await makeRequest<ResponseUnion>(endpoint);
                if (!res.success) {
                    return {
                        success: false,
                        error: res.error ?? 'Failed to fetch operations data',
                    };
                }
                const backend = res.data;
                // Case 1: normal envelope { success, data }
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: OperationsResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                // Case 2: payload returned directly (fallback)
                const isOperationsResponse = (obj: unknown): obj is OperationsResponse => {
                    return (
                        typeof obj === 'object' &&
                        obj !== null &&
                        'byHour' in obj &&
                        Array.isArray((obj as { byHour?: unknown }).byHour)
                    );
                };
                if (isOperationsResponse(backend)) {
                    return { success: true, data: backend };
                }
                return { success: false, error: 'Invalid operations response format' };
            },

            getInventoryTrends: async (params?: {
                startDate?: string;
                endDate?: string;
                currency?: 'EUR' | 'LEI';
            }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const endpoint = query
                    ? `/statistics/inventory-trends?${query}`
                    : '/statistics/inventory-trends';
                type BackendEnvelope = { success: boolean; data?: InventoryTrendsResponse };
                type ResponseUnion = BackendEnvelope | InventoryTrendsResponse;
                const res = await makeRequest<ResponseUnion>(endpoint);
                if (!res.success) {
                    return {
                        success: false,
                        error: res.error ?? 'Failed to fetch inventory trends',
                    };
                }
                const backend = res.data;
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: InventoryTrendsResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                const isInventoryTrendsResponse = (
                    obj: unknown,
                ): obj is InventoryTrendsResponse => {
                    return (
                        typeof obj === 'object' &&
                        obj !== null &&
                        'silver' in obj &&
                        Array.isArray((obj as { silver?: unknown }).silver)
                    );
                };
                if (isInventoryTrendsResponse(backend)) {
                    return { success: true, data: backend };
                }
                return { success: false, error: 'Invalid inventory trends response format' };
            },

            getExpensesData: async (params?: { startDate?: string; endDate?: string }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const res = await makeRequest<{ success: boolean; data?: ExpensesResponse }>(
                    query ? `/statistics/expenses?${query}` : '/statistics/expenses',
                );
                if (!res.success || !res.data?.data) {
                    return { success: false, error: res.error ?? 'Failed to fetch expenses' };
                }
                return { success: true, data: res.data.data };
            },

            getGoodsSalesData: async (params?: {
                startDate?: string;
                endDate?: string;
                currency?: 'EUR' | 'LEI';
            }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const res = await makeRequest<{ success: boolean; data?: GoodsSalesResponse }>(
                    query ? `/statistics/goods-sales?${query}` : '/statistics/goods-sales',
                );
                if (!res.success || !res.data?.data) {
                    return { success: false, error: res.error ?? 'Failed to fetch goods sales' };
                }
                return { success: true, data: res.data.data };
            },

            getGoldValueData: async (params?: {
                startDate?: string;
                endDate?: string;
                timeframe?: StatisticsTimeframe;
            }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const endpoint = query
                    ? `/statistics/gold-value?${query}`
                    : '/statistics/gold-value';
                type BackendEnvelope = { success: boolean; data?: GoldValueResponse };
                type ResponseUnion = BackendEnvelope | GoldValueResponse;
                const res = await makeRequest<ResponseUnion>(endpoint);
                if (!res.success) {
                    return {
                        success: false,
                        error: res.error ?? 'Failed to fetch gold value data',
                    };
                }
                const backend = res.data;
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: GoldValueResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                const isGoldValueResponse = (obj: unknown): obj is GoldValueResponse => {
                    return (
                        typeof obj === 'object' &&
                        obj !== null &&
                        'timeSeries' in obj &&
                        'snapshot' in obj
                    );
                };
                if (isGoldValueResponse(backend)) {
                    return { success: true, data: backend };
                }
                return { success: false, error: 'Invalid gold value response format' };
            },

            getGoldPriceSimulation: async (params?: { currency?: 'EUR' | 'LEI' }) => {
                const query = params ? new URLSearchParams(params).toString() : '';
                const endpoint = query
                    ? `/statistics/gold-price-simulation?${query}`
                    : '/statistics/gold-price-simulation';
                type BackendEnvelope = {
                    success: boolean;
                    data?: GoldPriceChangeSimulationResponse;
                };
                type ResponseUnion = BackendEnvelope | GoldPriceChangeSimulationResponse;
                const res = await makeRequest<ResponseUnion>(endpoint);
                if (!res.success) {
                    return {
                        success: false,
                        error: res.error ?? 'Failed to fetch gold price simulation data',
                    };
                }
                const backend = res.data;
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: GoldPriceChangeSimulationResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                const isGoldPriceSimulationResponse = (
                    obj: unknown,
                ): obj is GoldPriceChangeSimulationResponse => {
                    return (
                        typeof obj === 'object' &&
                        obj !== null &&
                        'karatBreakdown' in obj &&
                        'summary' in obj
                    );
                };
                if (isGoldPriceSimulationResponse(backend)) {
                    return { success: true, data: backend };
                }
                return {
                    success: false,
                    error: 'Invalid gold price simulation response format',
                };
            },

            getCashDemandProjection: async (params?: {
                priceChangePercent?: number;
                monthsAhead?: number;
                currency?: 'EUR' | 'LEI';
            }) => {
                const queryParams = new URLSearchParams();
                if (params?.priceChangePercent !== undefined) {
                    queryParams.set('priceChangePercent', String(params.priceChangePercent));
                }
                if (params?.monthsAhead !== undefined) {
                    queryParams.set('monthsAhead', String(params.monthsAhead));
                }
                if (params?.currency) {
                    queryParams.set('currency', params.currency);
                }
                const queryString = queryParams.toString();
                const suffix = queryString ? `?${queryString}` : '';
                const url = `/statistics/cash-demand-projection${suffix}`;

                type BackendEnvelope = {
                    success: boolean;
                    data?: CashDemandProjectionResponse;
                };
                type ResponseUnion = BackendEnvelope | CashDemandProjectionResponse;
                const res = await makeRequest<ResponseUnion>(url);
                if (!res.success) {
                    return {
                        success: false,
                        error: res.error ?? 'Failed to fetch cash demand projection',
                    };
                }
                const backend = res.data;
                if (
                    backend &&
                    typeof backend === 'object' &&
                    'data' in backend &&
                    typeof (backend as { data?: unknown }).data !== 'undefined'
                ) {
                    const payload = (backend as { data?: CashDemandProjectionResponse }).data;
                    if (payload) {
                        return { success: true, data: payload };
                    }
                }
                const isCashDemandResponse = (
                    obj: unknown,
                ): obj is CashDemandProjectionResponse => {
                    return (
                        typeof obj === 'object' &&
                        obj !== null &&
                        'months' in obj &&
                        'totalAdditionalCash' in obj
                    );
                };
                if (isCashDemandResponse(backend)) {
                    return { success: true, data: backend };
                }
                return {
                    success: false,
                    error: 'Invalid cash demand projection response format',
                };
            },

            refresh: async (options?: { force?: boolean }) => {
                return makeRequest<StatisticsRefreshResponse>('/statistics/refresh', {
                    method: 'POST',
                    body: JSON.stringify({ force: options?.force === true }),
                });
            },
        };

        const configurationApi: ApiContextType['configuration'] = {
            getAll: async () => {
                // Backend returns envelope { success, data: Record<string,string> }
                type BackendEnvelope = {
                    success: boolean;
                    data?: Record<string, string>;
                    error?: string;
                };
                type ResponseUnion = BackendEnvelope | Record<string, string>;
                const res = await makeRequest<ResponseUnion>('/configuration');
                if (!res.success) return { success: false, error: res.error };
                const payload: ResponseUnion | undefined = res.data;
                const isBackendEnvelope = (obj: unknown): obj is BackendEnvelope =>
                    typeof obj === 'object' && obj !== null && 'data' in obj;
                if (isBackendEnvelope(payload) && payload.data) {
                    return { success: true, data: payload.data };
                }
                // Fallback to direct map if server ever returns it directly
                return { success: true, data: (payload ?? {}) as Record<string, string> };
            },

            get: async (denumire: string) => {
                // Backend returns envelope { success, data: { configName, configValue } }
                type BackendData = { configName: string; configValue: string };
                type BackendEnvelope = {
                    success: boolean;
                    data?: BackendData;
                    error?: string;
                };
                type ResponseUnion = BackendEnvelope | BackendData;
                const res = await makeRequest<ResponseUnion>(`/configuration/${denumire}`);
                if (!res.success) return { success: false, error: res.error };
                const payload: ResponseUnion | undefined = res.data;
                const isBackendEnvelope = (obj: unknown): obj is BackendEnvelope =>
                    typeof obj === 'object' && obj !== null && 'data' in obj;
                const mapToFrontend = (raw: BackendData) => ({
                    denumire: raw.configName,
                    valoare: raw.configValue,
                });
                if (isBackendEnvelope(payload) && payload.data) {
                    return { success: true, data: mapToFrontend(payload.data) };
                }
                // Fallback to direct object if server ever returns it directly
                return { success: true, data: mapToFrontend(payload as BackendData) };
            },

            update: async (denumire: string, valoare: string) => {
                type BackendEnvelope = {
                    success: boolean;
                    data?: { denumire: string; valoare: string };
                    error?: string;
                };
                type ResponseUnion = BackendEnvelope | { denumire: string; valoare: string };
                const res = await makeRequest<ResponseUnion>(`/configuration/${denumire}`, {
                    method: 'PUT',
                    body: JSON.stringify({ valoare }),
                });
                if (!res.success) return { success: false, error: res.error };
                const payload: ResponseUnion | undefined = res.data;
                const isBackendEnvelope = (obj: unknown): obj is BackendEnvelope =>
                    typeof obj === 'object' && obj !== null && 'data' in obj;
                if (isBackendEnvelope(payload) && payload.data) {
                    return { success: true, data: payload.data };
                }
                return { success: true, data: payload as { denumire: string; valoare: string } };
            },

            updateBatch: async (configurations: Array<{ denumire: string; valoare: string }>) => {
                type BackendEnvelope = {
                    success: boolean;
                    data?: Array<{ denumire: string; valoare: string }>;
                    error?: string;
                };
                type ResponseUnion = BackendEnvelope | Array<{ denumire: string; valoare: string }>;
                const res = await makeRequest<ResponseUnion>('/configuration/batch', {
                    method: 'POST',
                    body: JSON.stringify({ configurations }),
                });
                if (!res.success) return { success: false, error: res.error };
                const payload: ResponseUnion | undefined = res.data;
                const isBackendEnvelope = (obj: unknown): obj is BackendEnvelope =>
                    typeof obj === 'object' && obj !== null && 'data' in obj;
                if (isBackendEnvelope(payload) && payload.data) {
                    return { success: true, data: payload.data };
                }
                return {
                    success: true,
                    data: (payload ?? []) as Array<{ denumire: string; valoare: string }>,
                };
            },

            sendSmtpTest: async (to: string) => {
                const res = await makeRequest<{ success: boolean }>('/configuration/smtp/test', {
                    method: 'POST',
                    body: JSON.stringify({ to }),
                });
                if (!res.success) {
                    return { success: false, error: res.error };
                }
                return { success: true };
            },

            getNextInvoiceNumber: async () => {
                const res = await makeRequest<{
                    success: boolean;
                    data?: { nextInvoiceNumber: string };
                    error?: string;
                }>('/configuration/next-invoice-number');
                if (!res.success) {
                    return { success: false, error: res.error };
                }
                if (res.data?.data === undefined) {
                    return { success: false, error: res.data?.error ?? 'Malformed response' };
                }
                return { success: true, data: res.data.data };
            },

            getLabelPrinter: async (cashPointId: number) => {
                const res = await makeRequest<CashPointConfigEnvelope<LabelPrinterConfigPayload>>(
                    `/configuration/label-printer/${cashPointId}`,
                );

                return unwrapCashPointConfig(res);
            },

            saveLabelPrinter: async (
                cashPointId: number,
                body: { queue: string; stockId: string | null },
            ) => {
                const res = await makeRequest<CashPointConfigEnvelope<LabelPrinterConfigPayload>>(
                    `/configuration/label-printer/${cashPointId}`,
                    { method: 'PUT', body: JSON.stringify(body) },
                );

                return unwrapCashPointConfig(res);
            },

            getCashPointAlerts: async (cashPointId: number) => {
                const res = await makeRequest<
                    CashPointConfigEnvelope<CashPointAlertSettingsPayload>
                >(`/configuration/cash-point-alerts/${cashPointId}`);

                return unwrapCashPointConfig(res);
            },

            saveCashPointAlerts: async (
                cashPointId: number,
                body: CashPointAlertSettingsPayload,
            ) => {
                const res = await makeRequest<
                    CashPointConfigEnvelope<CashPointAlertSettingsPayload>
                >(`/configuration/cash-point-alerts/${cashPointId}`, {
                    method: 'PUT',
                    body: JSON.stringify(body),
                });

                return unwrapCashPointConfig(res);
            },

            getLoyalty: async () => {
                const res = await makeRequest<CashPointConfigEnvelope<LoyaltySettingsPayload>>(
                    '/configuration/loyalty',
                );

                return unwrapCashPointConfig(res);
            },

            saveLoyalty: async (body: LoyaltySettingsPayload) => {
                const res = await makeRequest<CashPointConfigEnvelope<LoyaltySettingsPayload>>(
                    '/configuration/loyalty',
                    { method: 'PUT', body: JSON.stringify(body) },
                );

                return unwrapCashPointConfig(res);
            },
        };

        const contractDocumentApi: ApiContextType['contractDocument'] = {
            getSettings: async () => {
                type SettingsPayload = {
                    engine: ContractDocumentEngine;
                    availableEngines: ContractDocumentEngine[];
                    supportedEngines: ContractDocumentEngine[];
                };
                return fetchDocumentSettings<SettingsPayload>('/contract-document/settings');
            },
            updateSettings: async (engine: ContractDocumentEngine) => {
                type UpdatePayload = { engine: ContractDocumentEngine };
                return updateDocumentSettings<UpdatePayload>('/contract-document/settings', engine);
            },
            getActiveTemplate: async () => {
                type Payload = {
                    template: ContractDocumentTemplatePayload;
                    assets: ContractDocumentAssetPayload[];
                };
                const res = await makeRequest<Payload>('/contract-document/templates/active');
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            listTemplates: async () => {
                type Payload = { templates: ContractDocumentTemplatePayload[] };
                const res = await makeRequest<Payload>('/contract-document/templates');
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            getTemplate: async templateId => {
                type Payload = {
                    template: ContractDocumentTemplatePayload;
                    assets: ContractDocumentAssetPayload[];
                };
                const res = await makeRequest<Payload>(
                    `/contract-document/templates/${templateId}`,
                );
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            saveDraft: async (templateId, input) => {
                type Payload = { template: ContractDocumentTemplatePayload };
                const res = await makeRequest<Payload>(
                    `/contract-document/templates/${templateId}/draft`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(input),
                    },
                );
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            duplicate: async (templateId, input) => {
                type Payload = {
                    template: ContractDocumentTemplatePayload;
                    assets: ContractDocumentAssetPayload[];
                };
                const res = await makeRequest<Payload>(
                    `/contract-document/templates/${templateId}/duplicate`,
                    {
                        method: 'POST',
                        body: JSON.stringify(input),
                    },
                );
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            publish: async templateId => {
                type Payload = { template: ContractDocumentTemplatePayload };
                const res = await makeRequest<Payload>(
                    `/contract-document/templates/${templateId}/publish`,
                    { method: 'POST' },
                );
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            restoreDefault: async (templateId, defaults) => {
                type Payload = { template: ContractDocumentTemplatePayload };
                const res = await makeRequest<Payload>(
                    `/contract-document/templates/${templateId}/restore-default`,
                    {
                        method: 'POST',
                        body: JSON.stringify(defaults),
                    },
                );
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            getFactoryDefault: async () => {
                type Payload = { name: string; htmlContent: string; cssContent: string | null };
                const res = await makeRequest<Payload>(
                    '/contract-document/templates/factory-default',
                );
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            listAssets: async templateId => {
                type Payload = { assets: ContractDocumentAssetPayload[] };
                const res = await makeRequest<Payload>(
                    `/contract-document/templates/${templateId}/assets`,
                );
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            uploadAsset: async (templateId, input) => {
                type Payload = { asset: ContractDocumentAssetPayload };
                const res = await makeRequest<Payload>(
                    `/contract-document/templates/${templateId}/assets`,
                    {
                        method: 'POST',
                        body: JSON.stringify(input),
                    },
                );
                if (!res.success || !res.data) {
                    return { success: false, error: res.error ?? 'No data' };
                }
                return { success: true, data: res.data };
            },
            deleteAsset: async (templateId, assetId) => {
                const res = await makeRequest<void>(
                    `/contract-document/templates/${templateId}/assets/${assetId}`,
                    { method: 'DELETE' },
                );
                if (!res.success) {
                    return { success: false, error: res.error };
                }
                return { success: true, data: undefined };
            },
            previewByNr: async (nr: number) => {
                const url = `${API_BASE_URL}/contract-document/preview?nr=${nr}`;
                try {
                    const response = await fetch(url, {
                        headers: {
                            ...(token && { Authorization: `Bearer ${token}` }),
                            Accept: 'application/pdf',
                        },
                    });
                    if (!response.ok) {
                        let message = `HTTP error! status: ${response.status}`;
                        try {
                            const errBody = await response.json();
                            if (typeof errBody?.message === 'string') message = errBody.message;
                            else if (typeof errBody?.error === 'string') message = errBody.error;
                        } catch {
                            // ignore non-JSON error bodies
                        }
                        return { success: false, error: message };
                    }
                    const blob = await response.blob();
                    return { success: true, data: blob };
                } catch (error) {
                    return { success: false, error: (error as Error).message };
                }
            },
        };

        const invoiceDocumentApi: ApiContextType['invoiceDocument'] = {
            getSettings: async () => {
                type SettingsPayload = {
                    engine: InvoiceDocumentEngine;
                    availableEngines: InvoiceDocumentEngine[];
                    supportedEngines: InvoiceDocumentEngine[];
                };
                return fetchDocumentSettings<SettingsPayload>('/invoice-document/settings');
            },
            updateSettings: async (engine: InvoiceDocumentEngine) => {
                type UpdatePayload = { engine: InvoiceDocumentEngine };
                return updateDocumentSettings<UpdatePayload>('/invoice-document/settings', engine);
            },
            getActiveTemplate: async () => {
                type Payload = { template: InvoiceDocumentTemplatePayload };
                const response = await makeRequest<Payload>('/invoice-document/templates/active');
                return response.success && response.data
                    ? { success: true, data: response.data }
                    : { success: false, error: response.error ?? 'No data' };
            },
            listTemplates: async () => {
                type Payload = { templates: InvoiceDocumentTemplatePayload[] };
                const response = await makeRequest<Payload>('/invoice-document/templates');
                return response.success && response.data
                    ? { success: true, data: response.data }
                    : { success: false, error: response.error ?? 'No data' };
            },
            getTemplate: async templateId => {
                type Payload = { template: InvoiceDocumentTemplatePayload };
                const response = await makeRequest<Payload>(
                    `/invoice-document/templates/${templateId}`,
                );
                return response.success && response.data
                    ? { success: true, data: response.data }
                    : { success: false, error: response.error ?? 'No data' };
            },
            saveDraft: async (templateId, input) => {
                type Payload = { template: InvoiceDocumentTemplatePayload };
                const response = await makeRequest<Payload>(
                    `/invoice-document/templates/${templateId}/draft`,
                    { method: 'PUT', body: JSON.stringify(input) },
                );
                return response.success && response.data
                    ? { success: true, data: response.data }
                    : { success: false, error: response.error ?? 'No data' };
            },
            duplicate: async (templateId, input) => {
                type Payload = { template: InvoiceDocumentTemplatePayload };
                const response = await makeRequest<Payload>(
                    `/invoice-document/templates/${templateId}/duplicate`,
                    { method: 'POST', body: JSON.stringify(input) },
                );
                return response.success && response.data
                    ? { success: true, data: response.data }
                    : { success: false, error: response.error ?? 'No data' };
            },
            publish: async templateId => {
                type Payload = { template: InvoiceDocumentTemplatePayload };
                const response = await makeRequest<Payload>(
                    `/invoice-document/templates/${templateId}/publish`,
                    { method: 'POST' },
                );
                return response.success && response.data
                    ? { success: true, data: response.data }
                    : { success: false, error: response.error ?? 'No data' };
            },
            restoreDefault: async (templateId, defaults) => {
                type Payload = { template: InvoiceDocumentTemplatePayload };
                const response = await makeRequest<Payload>(
                    `/invoice-document/templates/${templateId}/restore-default`,
                    { method: 'POST', body: JSON.stringify(defaults) },
                );
                return response.success && response.data
                    ? { success: true, data: response.data }
                    : { success: false, error: response.error ?? 'No data' };
            },
            getFactoryDefault: async () => {
                type Payload = { name: string; htmlContent: string; cssContent: string | null };
                const response = await makeRequest<Payload>(
                    '/invoice-document/templates/factory-default',
                );
                return response.success && response.data
                    ? { success: true, data: response.data }
                    : { success: false, error: response.error ?? 'No data' };
            },
            previewByInvoiceNumber: async (invoiceNumber, templateId, documentType) => {
                const params = new URLSearchParams({ invoiceNumber });
                if (templateId !== undefined) params.set('templateId', String(templateId));
                if (documentType) params.set('documentType', documentType);
                const url = `${API_BASE_URL}/invoice-document/preview?${params.toString()}`;
                return fetchPdfBlob(url);
            },
        };

        return {
            makeRequest,
            inventory: inventoryApi,
            metals: metalsApi,
            reports: reportsApi,
            payments: paymentsApi,
            stats: statsApi,
            menu: menuApi,
            controlRoom: controlRoomApi,
            configuration: configurationApi,
            invoiceDocument: invoiceDocumentApi,
            contractDocument: contractDocumentApi,
            statuts: statutsApi,
            statistics: statisticsApi,
        };
    }, [token, API_BASE_URL, refreshToken, logout]);

    return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};
